import { type PropsWithChildren } from "react";

import { Button } from "#components/Button";
import { Tooltip } from "#components/Tooltip";
import { useCopyToClipboard } from "#hooks";
import { CopyIcon } from "#icons";
import { customTv } from "#utils";

export interface CopyButtonProps extends PropsWithChildren {
  /** Overrides the default aria-label (`Copy ${copyLabel} button` or `Copy value button`). */
  "aria-label"?: string;
  className?: string;
  copyLabel?: string;
  /**
   * Controls where the copy control and its status tooltip are attached.
   * - `container` (default): children render inside the button; tooltip covers the whole button
   * - `icon`: children render next to an icon-only button; tooltip appears only on the icon
   *   button (use this when children contain interactive elements such as links)
   * - `none`: like `container` but without the built-in tooltip
   */
  tooltipTarget?: "container" | "icon" | "none";
  value: string;
  variant?: "stacklet-orange"; // color variants
}

const copyButtonStyles = customTv({
  base: "hover:cursor-pointer",
  variants: {
    variant: { "stacklet-orange": "text-stacklet-orange-700" },
  },
});

const copyContainerStyles = customTv({
  base: "flex w-fit items-center gap-xxs",
  variants: {
    variant: { "stacklet-orange": "text-stacklet-orange-700" },
  },
});

export function CopyButton({
  "aria-label": ariaLabel,
  children,
  className,
  copyLabel,
  tooltipTarget = "container",
  value,
  variant,
}: CopyButtonProps) {
  const [copyValueStatus, copyValue] = useCopyToClipboard(value);

  let status = `Copy ${copyLabel ?? "value"}`;
  if (copyValueStatus === "copied") {
    status = "Copied";
  } else if (copyValueStatus === "failed") {
    status = "Copy failed!";
  }

  const label = ariaLabel ?? `Copy ${copyLabel ?? "value"} button`;
  // An empty string still counts as children for Button, which would render an
  // empty label span and its flex gap before the icon.
  const content = children === "" ? null : children;

  // In icon mode children stay outside the button and the caller's className
  // styles the surrounding container instead of the button.
  const isIconTarget = tooltipTarget === "icon";

  const buttonEl = (
    <Button
      IconRight={<CopyIcon />}
      aria-label={label}
      className={copyButtonStyles({ class: isIconTarget ? undefined : className, variant })}
      onPress={() => copyValue()}
      variant="tertiary"
      preventFocusOnPress
    >
      {isIconTarget ? null : content}
    </Button>
  );

  if (isIconTarget) {
    return (
      <div className={copyContainerStyles({ class: className, variant })}>
        {content}
        <Tooltip shouldCloseOnPress={false} title={status} asChild>
          {buttonEl}
        </Tooltip>
      </div>
    );
  }

  if (tooltipTarget === "none") {
    return buttonEl;
  }

  return (
    <Tooltip shouldCloseOnPress={false} title={status} asChild>
      {buttonEl}
    </Tooltip>
  );
}
